import axios from "axios";
import { createBrowserHistory } from "history";
import { toast } from "react-toastify";
import { decryptResponse, encryptPayload } from "./encryption_decryption";
const history = createBrowserHistory();
export const baseURL = process.env.REACT_APP_BE_ENDPOINT;

// `status` and `data` are bolted onto every error body by the backend's
// custom_exception_handler; they are envelope, not messages.
const ERROR_ENVELOPE_KEYS = ["status", "data"];
// Long enough for any validation message, short enough to reject a gateway's
// HTML error page, which would otherwise become one enormous toast.
const MAX_ERROR_MESSAGE_LENGTH = 300;

const flattenMessages = (value) => {
  if (value === null || value === undefined) return [];
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return value.flatMap(flattenMessages);
  if (typeof value !== "object") return [String(value)];
  return Object.entries(value)
    .filter(([key]) => !ERROR_ENVELOPE_KEYS.includes(key))
    .flatMap(([, nested]) => flattenMessages(nested));
};

/**
 * A 400 reaches the browser in two shapes. A view that builds its own Response
 * sends `{detail: "..."}`. A serializer raising ValidationError goes through
 * DRF, and custom_exception_handler passes those errors through un-wrapped —
 * `{field: ["..."], status, data}`, with no `detail` key at all. Reading
 * `data.detail` blindly threw a TypeError on that second shape, from inside the
 * error handler, so the caller lost the real error and the user saw no message.
 */
export const collectErrorMessages = (body) =>
  flattenMessages(body?.detail ?? body)
    .map((message) => (typeof message === "string" ? message.trim() : ""))
    .filter((message) => message.length > 0 && message.length <= MAX_ERROR_MESSAGE_LENGTH);

const isSecuredModule = (url) => {
  // A transport failure (offline, DNS, CORS, timeout) reaches the rejection
  // handler with no `response`, so there is no url to test. Reading `.match` on
  // it threw a TypeError inside the interceptor itself, replacing the axios
  // error with a TypeError for every caller in the app.
  const isSecureEndpoint = url?.match?.(/(customers|auctions|billings|contracts)\//g);
  return process.env.REACT_APP_API_ENCRYPTION_DECRYPTION === "true" && isSecureEndpoint;
};

const API = axios.create({
  baseURL,
  responseType: "json",
});

export default API;
// Axios Request interceptors
API.interceptors.request.use((req) => {
  const userToken = localStorage.getItem("token");
  if (userToken)
    req.headers = {
      Authorization: "token " + userToken.replace(/^"|"$/g, ""),
    };

  const url = req?.url;
  const securedModule = isSecuredModule(url);
  if (securedModule) {
    if (req.method === "get") {
      req.params = encryptPayload(req.params);
    }
    const formDataObject = {};
    if (req?.data instanceof FormData) {
      for (const [key, value] of req.data.entries()) {
        formDataObject[key] = value;
      }
      req.data = formDataObject;
    }
    req.data = encryptPayload(req.data);
  }
  return req;
});

API.interceptors.response.use(
  (res) => {
    const url = res?.config?.url;
    const securedModule = isSecuredModule(url);
    if (securedModule) res.data = decryptResponse(res.data);
    return res;
  },
  (err) => {
    const url = err.response?.config?.url;
    const securedModule = isSecuredModule(url);
    if (securedModule) err.response.data = decryptResponse(err.response.data);
    if (err?.response?.status === 400) {
      collectErrorMessages(err?.response?.data).forEach((message) => toast.error(message));
    }
    if (err?.response?.status === 404) {
      toast.error(err?.response?.data?.detail);
    }
    if (err?.response?.status === 422) {
      if (err && err.response && err.response.data && err.response.data.errors && err.response.data.errors.length > 0) {
        err.response.data.errors.map((text, i) => {
          return Object.keys(text).forEach(function (key) {
            toast.error(err?.response?.data?.detail);
          });
        });
      } else {
        toast.error(err?.response?.data?.detail);
      }
    }
    if (err?.response?.status === 500) {
      toast.error(err?.response?.data?.detail);
    }
    if (err?.response?.status === 403) {
      toast.error(err?.response?.data?.detail);
    }
    if (err?.response?.status === 405) {
      toast.error(err?.response?.data?.detail);
    }
    if (err?.response?.status === 401) {
      toast.error(err?.response?.data?.detail);
      localStorage.removeItem("token");
      history.push("/login");
      window.location.reload();
    }
    return err;
  }
);
